import {useEffect, useState} from 'react'

import {getFilmFrameReader, readFilmStill} from '#/lib/flashbookFilmFrames'

/**
 * A book's cover still, read from the cover film when the record has none.
 *
 * Publishing derives a still and writes it onto the cover (see
 * `#/components/flashbooks/flashbookSave`), so every book that goes out from now on
 * carries a picture and this hook does nothing. It exists for the books already
 * out there: a film cover published before the record had a field for its frame,
 * whose only picture of itself *is* the film. Without this, such a book is a flat
 * rectangle of theme colour everywhere it is not being played — which is how it
 * was reported, on a real published book, in a real feed.
 *
 * Read once, at the minute the cover opens on, through the same reader the studio's
 * trimmer uses. Two things follow from that, and both are deliberate:
 *
 *  - **A card that cannot read the film keeps its themed fallback.** A film that
 *    will not decode, or one served without the headers a canvas needs, yields
 *    nothing; the cover then draws the book's own colours, which is a card, not a
 *    broken one.
 *  - **The card holds the picture, not the decoder.** The reader lets the film go
 *    when the card does — a feed scrolls past hundreds of rows and must not leave a
 *    film element behind for each one. Scrolling back re-reads, and that read is
 *    free: the platform's own HTTP cache already holds the bytes.
 */
export function useFlashBookFilmPoster(
  uri: string | undefined,
  atMs: number = 0,
): string | undefined {
  const [still, setStill] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!uri) {
      setStill(undefined)
      return
    }
    /*
     * A generation guard, because a read outlives what asked for it: a card can be
     * recycled by the feed mid-read, and the row underneath must not be painted with
     * the previous book's cover.
     */
    let live = true
    void readFilmStill(uri, atMs).then(
      read => {
        if (live) setStill(read)
      },
      () => {
        if (live) setStill(undefined)
      },
    )
    return () => {
      live = false
    }
  }, [uri, atMs])

  useEffect(() => {
    if (!uri) return
    const reader = getFilmFrameReader()
    return () => reader.release(uri)
  }, [uri])

  return still
}
